import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Dropdown - dropdown-01 tasarımına uygun seçim bileşeni.
 * Açılır liste portal ile body'e render edilir; overflow'lı üst container'lar kesmez.
 */
export function Dropdown({
  items = [],
  value,
  onChange,
  placeholder = 'Seçiniz',
  className,
  buttonClassName,
  simple = false,
  iconLeft,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [panelRect, setPanelRect] = useState({ top: 0, left: 0, width: 0 });
  const [openDirection, setOpenDirection] = useState('down');

  const selectedItem = items.find((i) => i.value === value);
  const displayLabel = selectedItem?.label ?? placeholder;

  const updatePanelRect = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const estimatedPanelHeight = Math.min(items.length * 48, 300);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUp = spaceBelow < estimatedPanelHeight + 16 && spaceAbove > spaceBelow;

      setOpenDirection(shouldOpenUp ? 'up' : 'down');
      if (shouldOpenUp) {
        setPanelRect({
          bottom: window.innerHeight - rect.top + 8,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.min(estimatedPanelHeight, spaceAbove - 16),
        });
      } else {
        setPanelRect({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width,
          maxHeight: spaceBelow - 16,
        });
      }
    }
  };

  const dropdownId = useRef(`dropdown-${Math.random().toString(36).slice(2, 9)}`).current;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current) return;
      const inButton = containerRef.current.contains(e.target);
      const panel = document.getElementById(dropdownId);
      const inPanel = panel && panel.contains(e.target);
      if (!inButton && !inPanel) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownId]);

  useLayoutEffect(() => {
    if (isOpen && containerRef.current) updatePanelRect();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updatePanelRect, true);
    window.addEventListener('resize', updatePanelRect);
    return () => {
      window.removeEventListener('scroll', updatePanelRect, true);
      window.removeEventListener('resize', updatePanelRect);
    };
  }, [isOpen]);

  const handleSelect = (item) => {
    onChange?.(item.value);
    setIsOpen(false);
  };

  const animY = openDirection === 'up' ? 8 : -8;

  const panelContent =
    isOpen &&
    createPortal(
      <AnimatePresence>
        <motion.div
          id={dropdownId}
          initial={{ opacity: 0, y: animY }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: animY }}
          transition={{ duration: 0.2 }}
          className={cn(
            'fixed z-[9999] min-w-[200px] rounded-xl border-2 overflow-auto shadow-xl',
            'bg-card border-border'
          )}
          style={{
            left: panelRect.left,
            width: panelRect.width,
            maxHeight: panelRect.maxHeight,
            ...(openDirection === 'up'
              ? { bottom: panelRect.bottom }
              : { top: panelRect.top }
            ),
          }}
        >
          {items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">Seçenek yok</div>
          ) : (
            items.map((item, index) => (
              <motion.button
                key={item.value}
                type="button"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.15, delay: index * 0.03 }}
                onClick={() => handleSelect(item)}
                className={cn(
                  'w-full px-4 py-3 text-left transition-colors duration-200',
                  'text-foreground hover:bg-primary hover:text-primary-foreground',
                  'flex items-center justify-between gap-3',
                  index !== items.length - 1 && 'border-b border-border'
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</div>
                  {!simple && item.description && (
                    <div className="text-sm text-muted-foreground truncate">{item.description}</div>
                  )}
                </div>
                {value === item.value && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className="shrink-0"
                  >
                    <Check className="w-5 h-5" />
                  </motion.div>
                )}
              </motion.button>
            ))
          )}
        </motion.div>
      </AnimatePresence>,
      document.body
    );

  return (
    <div ref={containerRef} data-dropdown-id={dropdownId} className={cn('relative min-w-[200px]', className)}>
      <motion.button
        type="button"
        disabled={disabled}
        whileHover={!disabled ? { scale: 1.01 } : undefined}
        whileTap={!disabled ? { scale: 0.99 } : undefined}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) updatePanelRect();
          }
        }}
        className={cn(
          'relative w-full px-4 py-3 rounded-xl border-2 transition-all duration-300',
          'bg-card border-border text-foreground',
          !disabled && 'hover:bg-muted/50 hover:border-primary/50',
          disabled && 'opacity-60 cursor-not-allowed',
          'flex items-center justify-between gap-2',
          iconLeft && 'pl-11',
          buttonClassName
        )}
      >
        {iconLeft && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground shrink-0 flex items-center">
            {iconLeft}
          </span>
        )}
        <span className="font-medium truncate text-left flex-1 whitespace-nowrap">{displayLabel}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        </motion.div>
      </motion.button>
      {panelContent}
    </div>
  );
}
